// The words and the glyph the document controls put on a file: what it accepts, how big it is, what it is, whether
// it has been read, and how many of its parts there are. Pure — no React — so the copy is testable and the
// components only lay it out. The kind and the status are the API's reading; nothing here decides them.
import type { DocumentKind, DocumentQuery, DocumentSummary, DocumentTextStatus } from '../../types/documents'

/** Every kind the API accepts, as the file picker's accept list. One list, one place. */
export const DOCUMENT_EXTENSIONS = ['.docx', '.doc', '.pptx', '.ppt', '.xlsx', '.xls', '.xlsm', '.csv', '.pdf', '.txt', '.md', '.eml', '.msg', '.png', '.jpg', '.jpeg', '.gif', '.webp'] as const
export const DOCUMENT_ACCEPT = DOCUMENT_EXTENSIONS.join(',')
export const DOCUMENT_MAX_BYTES = 64 * 1024 * 1024

/** What the drop zone says it takes, in the person's words rather than extensions. */
export const DOCUMENT_HINT = 'Word, PowerPoint, Excel, CSV, PDF, text, mail (.eml, .msg) or an image · up to 64 MB'

/** "340 KB", "1.2 MB", "64 MB": one decimal under ten, none above. */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return ''
  if (bytes < 1024) return `${bytes} B`
  const kb = bytes / 1024
  if (kb < 1024) return `${Math.round(kb)} KB`
  const mb = kb / 1024
  if (mb < 10) return `${Math.round(mb * 10) / 10} MB`
  if (mb < 1024) return `${Math.round(mb)} MB`
  return `${Math.round((mb / 1024) * 10) / 10} GB`
}

/** The status in a word: reading… while the job runs, read when it landed, else the failure by name. */
export function statusWord(status: DocumentTextStatus): string {
  switch (status) {
    case 'pending': return 'reading…'
    case 'extracted': return 'read'
    case 'unreadable': return 'unreadable'
    case 'failed': return 'failed'
  }
}

/** The kind in a word, for the tag and the tooltip. */
export const kindWords: Record<DocumentKind, string> = {
  deck: 'deck', sheet: 'sheet', doc: 'document', pdf: 'PDF', image: 'image', text: 'text', mail: 'mail', other: 'file',
}

/** A line icon for the kind when one fits; otherwise the kind's word, drawn as a small tag. */
export function kindGlyph(kind: DocumentKind): { icon: string } | { word: string } {
  switch (kind) {
    case 'deck': return { icon: 'deck' }
    case 'sheet': return { icon: 'sheet' }
    case 'doc':
    case 'text': return { icon: 'document' }
    case 'image': return { icon: 'photo' }
    case 'mail': return { icon: 'mail' }
    default: return { word: kindWords[kind] ?? kind }
  }
}

/** "12 slides", "3 sheets", "8 pages"; null when there is nothing to count (an image, or nothing read yet). */
export function partsWord(kind: DocumentKind, parts: number): string | null {
  if (!parts || parts < 1 || kind === 'image') return null
  const unit = kind === 'deck' ? 'slide' : kind === 'sheet' ? 'sheet' : 'page'
  return `${parts} ${unit}${parts === 1 ? '' : 's'}`
}

/** "4 facts", "1 fact", "no facts yet" while reading, "no facts" once read. */
export function factsWord(facts: number, status: DocumentTextStatus): string {
  if (facts > 0) return `${facts} fact${facts === 1 ? '' : 's'}`
  return status === 'pending' ? 'no facts yet' : 'no facts'
}

/** The query key of one list: the spoke and which of the three targets it is about. */
export function documentsKey(spoke: string, query: DocumentQuery): (string | null)[] {
  return ['documents', spoke, query.person ?? null, query.engagement ?? null, query.company ?? null]
}

/** The job to watch: the newest document still being read that names its job; undefined when none is. */
export function pendingJob(documents: DocumentSummary[] | null | undefined): string | undefined {
  return (documents ?? []).find((d) => d.textStatus === 'pending' && d.jobId)?.jobId ?? undefined
}
